import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo, useState, useCallback, useRef, memo } from "react";
import { IconDownload } from "@/Icon/Download";
import { LayoutHeader } from "@/Header";
import { useUserPhotoAlbum } from "~/services/customer/common";
import { Loading } from "@/Loading";
import { ImageViewer } from "@/ImageViewer";
// [성능 개선] 썸네일 비활성화로 사용 안함
// import { VideoThumbnail } from "@/common/VideoThumbnail";
import { useVirtualizer, type VirtualItem } from "@tanstack/react-virtual";

export const Route = createFileRoute("/customer/layout/gallery")({
  component: Gallery,
});

type MediaType = "image" | "video";

interface GalleryItem {
  date: string;
    images: {
      albumSeq: number;
      url: string;
      thumbnailUrl?: string | null;
      thumbnailStatus?: string | null;
      muteYn?: string | null;
      dtRegist: string;
      mediaType: MediaType;
    isSelected?: boolean;
  }[];
}

// URL 확장자로 미디어 타입 판단
const getMediaType = (url: string): MediaType => {
  const videoExtensions = [".mp4", ".mov", ".avi", ".webm", ".mkv", ".m4v"];
  const lowerUrl = url.toLowerCase();
  return videoExtensions.some((ext) => lowerUrl.includes(ext))
    ? "video"
    : "image";
};

// 메모이제이션된 미디어 아이템 컴포넌트
// [성능 개선] 동영상만 플레이스홀더 처리 (Canvas 썸네일 생성이 메모리 과다 사용)
// - 이미지: 원본 <img> 태그 사용 (lazy loading으로 최적화됨)
// - 동영상: 플레이스홀더 표시 (VideoThumbnail의 Canvas 디코딩이 ~32MB 사용)
const CustomerMediaItem = memo(({
  media,
  mediaIndex,
  isSelectMode,
  isSelected,
  onSelect,
}: {
  media: {
    albumSeq: number;
    url: string;
    thumbnailUrl?: string | null;
    thumbnailStatus?: string | null;
    mediaType: MediaType;
  };
  mediaIndex: number;
  isSelectMode: boolean;
  isSelected: boolean;
  onSelect: (albumSeq: number, url: string, mediaType: MediaType) => void;
}) => {
  const isThumbnailReady = (media.thumbnailStatus || "").toUpperCase() === "COMPLETE";
  const imagePreviewUrl = isThumbnailReady && media.thumbnailUrl ? media.thumbnailUrl : media.url;
  const videoPreviewUrl = isThumbnailReady && media.thumbnailUrl ? media.thumbnailUrl : null;
  return (
    <div
      className="relative cursor-pointer aspect-square"
      onClick={() => onSelect(media.albumSeq, media.url, media.mediaType)}
    >
      {media.mediaType === "video" ? (
        videoPreviewUrl ? (
          <img
            src={videoPreviewUrl}
            alt={`Gallery media ${mediaIndex + 1}`}
            className="object-cover w-full h-full rounded-lg bg-gray-100"
            loading="lazy"
            decoding="async"
          />
        ) : (
          <div className="flex justify-center items-center w-full h-full bg-gray-300 rounded-lg">
            <svg
              className="w-10 h-10 text-gray-500"
              fill="currentColor"
              viewBox="0 0 24 24"
            >
              <path d="M18 4l2 4h-3l-2-4h-2l2 4h-3l-2-4H8l2 4H7L5 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V4h-4z" />
            </svg>
          </div>
        )
      ) : (
        <img
          src={imagePreviewUrl}
          alt={`Gallery media ${mediaIndex + 1}`}
          className="object-cover w-full h-full rounded-lg bg-gray-100"
          loading="lazy"
          decoding="async"
        />
      )}
      {media.mediaType === "video" && (
        <div className="flex absolute inset-0 justify-center items-center bg-black bg-opacity-20 rounded-lg pointer-events-none">
          <svg
            className="w-12 h-12 text-white opacity-80"
            fill="currentColor"
            viewBox="0 0 24 24"
          >
            <path d="M8 5v14l11-7z" />
          </svg>
        </div>
      )}
      {isSelectMode && (
        <div className="absolute top-1 left-1">
          <div
            className={`w-4 h-4 rounded-full border-2 flex items-center justify-center
              ${isSelected ? "bg-orange-500 border-orange-500" : "border-white bg-white"}`}
          >
            {isSelected && <span className="text-sm text-white">✓</span>}
          </div>
        </div>
      )}
    </div>
  );
});

CustomerMediaItem.displayName = "CustomerMediaItem";

function Gallery() {
  const parentRef = useRef<HTMLDivElement>(null);

  const [isSelectMode, setIsSelectMode] = useState(false);
  const [selectedImages, setSelectedImages] = useState<Set<number>>(new Set());

  // MediaViewer 상태 (이미지/동영상)
  const [selectedMedia, setSelectedMedia] = useState<{
    url: string;
    type: MediaType;
  } | null>(null);
  const [_selectedAlbumSeq, setSelectedAlbumSeq] = useState<number | null>(
    null
  );
  const [isMediaViewerVisible, setIsMediaViewerVisible] = useState(false);
  const [currentMediaIndex, setCurrentMediaIndex] = useState<number>(0);

  // API 데이터 가져오기
  const { data: photoAlbumData, isLoading, error } = useUserPhotoAlbum();

  // API 데이터를 GalleryItem 형태로 변환 (메모이제이션)
  const galleryData: GalleryItem[] = useMemo(() => {
    return photoAlbumData?.data?.props?.map((prop) => ({
      date: prop.type,
      images: prop.datas.map((data) => ({
        albumSeq: data.albumSeq,
        url: data.fileUrl,
        thumbnailUrl: data.thumbnailUrl,
        thumbnailStatus: data.thumbnailStatus,
        muteYn: data.muteYn,
        dtRegist: data.dtRegist,
        mediaType: getMediaType(data.fileUrl),
      })),
    })) || [];
  }, [photoAlbumData]);

  // 모든 미디어 데이터 메모이제이션
  const allMedia = useMemo(() => {
    return galleryData.flatMap((group) => group.images);
  }, [galleryData]);

  // 모든 미디어 URL 메모이제이션
  const allMediaUrls = useMemo(() => {
    return allMedia.map((media) => media.url);
  }, [allMedia]);

  // 각 행의 ref를 저장할 Map
  const rowRefs = useRef<Map<number, HTMLDivElement>>(new Map());
  const imageLoadObserverRef = useRef<ResizeObserver | null>(null);

  // 가상화 설정 - 동적 높이 측정 사용
  const rowVirtualizer = useVirtualizer({
    count: galleryData.length,
    getScrollElement: () => parentRef.current,
    estimateSize: (index: number) => {
      // 이미지 개수에 따라 높이 추정
      const group = galleryData[index];
      if (!group) return 200;
      
      // 날짜 헤더 높이 (약 24px) + gap (8px) + padding-bottom (24px) = 56px
      // 이미지 그리드: 행 수 = Math.ceil(이미지 개수 / 4)
      // 각 행 높이 = (100vw - padding) / 4 (aspect-square) + gap
      const imageCount = group.images?.length || 0;
      const rows = Math.ceil(imageCount / 4);
      // 화면 너비에서 padding 제거 (px-4 = 16px * 2 = 32px)
      const availableWidth = typeof window !== 'undefined' ? window.innerWidth - 64 : 375;
      const imageRowHeight = (availableWidth / 4) + 8; // gap-2 = 8px
      const estimatedHeight = 56 + (rows * imageRowHeight);
      
      return Math.max(estimatedHeight, 100); // 최소 100px
    },
    overscan: 3, // 화면 밖에 미리 렌더링할 항목 수
    measureElement: (element: Element) => {
      // 실제 DOM 요소의 높이 측정
      if (!element) return 200;
      const height = element.getBoundingClientRect().height;
      return height > 0 ? height : 200;
    },
  });
  const userAgent = navigator.userAgent;
  const isIOS = useMemo(() => {
    // 일반적인 iOS 감지 + 앱 내 웹뷰 감지
    return /MT_I/i.test(userAgent);
  }, [userAgent]);
  const isAndroid = useMemo(() => {
    // 일반적인 안드로이드 감지 + 앱 내 웹뷰 감지
    return /MT_A/i.test(userAgent);
  }, [userAgent]);

  // 이미지/비디오 로드 후 높이 재측정을 위한 ResizeObserver 설정
  useEffect(() => {
    if (galleryData.length === 0) return;

    // ResizeObserver로 각 행의 크기 변경 감지
    imageLoadObserverRef.current = new ResizeObserver((entries) => {
      entries.forEach((entry) => {
        const element = entry.target as HTMLDivElement;
        const index = parseInt(element.dataset.index || '0', 10);
        if (!isNaN(index)) {
          // 높이 변경 시 가상화 라이브러리에 알림
          rowVirtualizer.measureElement(element);
        }
      });
    });

    // 모든 행 요소 관찰 시작
    rowRefs.current.forEach((el) => {
      if (imageLoadObserverRef.current) {
        imageLoadObserverRef.current.observe(el);
      }
    });

    // 초기 높이 측정 (이미지 로드 대기)
    const timeoutId = setTimeout(() => {
      rowRefs.current.forEach((el) => {
        rowVirtualizer.measureElement(el);
      });
    }, 100);

    return () => {
      if (imageLoadObserverRef.current) {
        imageLoadObserverRef.current.disconnect();
      }
      clearTimeout(timeoutId);
    };
  }, [galleryData, rowVirtualizer]);

  // Gallery 전용 뒤로가기 처리 함수를 전역에 등록
  useEffect(() => {
    const isAndroidApp = /MT_A/i.test(userAgent);

    if (isAndroidApp && typeof window !== "undefined") {
      // 커스텀 뒤로가기 처리 함수 (MediaViewer 열려있으면 닫기)
      window.customBackHandler = () => {
        console.log("customer gallery 커스텀 뒤로가기 처리");
        if (isMediaViewerVisible) {
          closeMediaViewer();
        } else {
          // 기본 핸들러로 위임 (앱 종료 다이얼로그 표시)
          window.customBackHandler = null;
          window.fnHistoryBack();
        }
      };

      console.log("customer gallery customBackHandler 등록");

      // 컴포넌트 언마운트 시 정리
      return () => {
        console.log("customer gallery 언마운트 - customBackHandler 제거");
        window.customBackHandler = null;
      };
    }
  }, [isMediaViewerVisible, userAgent]);

  // MediaViewer 닫기
  const closeMediaViewer = useCallback(() => {
    setIsMediaViewerVisible(false);
    setSelectedMedia(null);
    setSelectedAlbumSeq(null);
  }, []);

  // 미디어 다운로드 처리 (이미지/동영상)
  const handleMediaDownload = async () => {
    if (!selectedMedia) return;

    try {
      const isVideo = selectedMedia.type === "video";
      if (isAndroid) {
        // 안드로이드에서는 네이티브 함수 사용
        if (isVideo) {
          // 동영상 다운로드 (downloadVideo가 없으면 downloadImage 사용)
          if ((window.mung as any).downloadVideo) {
            (window.mung as any).downloadVideo(selectedMedia.url);
          } else {
            window.mung.downloadImage(selectedMedia.url);
          }
        } else {
          window.mung.downloadImage(selectedMedia.url);
        }
      } else if (isIOS) {
        // iOS에서는 네이티브 함수 사용
        if (isVideo) {
          window.webkit?.messageHandlers?.mung?.postMessage([
            "downloadVideo",
            selectedMedia.url,
          ]);
        } else {
          window.webkit?.messageHandlers?.mung?.postMessage([
            "downloadImage",
            selectedMedia.url,
          ]);
        }
      } else {
        // 웹에서는 fetch로 blob을 가져와서 다운로드 (CORS 문제 해결)
        try {
          const response = await fetch(selectedMedia.url);
          const blob = await response.blob();
          const downloadUrl = window.URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.href = downloadUrl;
          const extension = isVideo ? ".mp4" : ".jpg";
          a.download = `${isVideo ? "video" : "image"}-${currentMediaIndex + 1}-${Date.now()}${extension}`;
          a.target = "_blank";
          a.rel = "noopener noreferrer";
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          window.URL.revokeObjectURL(downloadUrl);
        } catch (fetchError) {
          // fetch 실패 시 새 탭에서 열기
          console.warn("다운로드 실패, 새 탭에서 열기:", fetchError);
          window.open(selectedMedia.url, "_blank");
        }
      }
    } catch (error) {
      console.error("다운로드 중 오류 발생:", error);
    }
  };

  // 개별 미디어 선택 (이미지/동영상)
  const toggleMediaSelection = useCallback((
    albumSeq: number,
    mediaUrl: string,
    mediaType: MediaType
  ) => {
    if (isSelectMode) {
      setSelectedImages((prev) => {
        const newSelected = new Set(prev);
        if (prev.has(albumSeq)) {
          newSelected.delete(albumSeq);
        } else {
          newSelected.add(albumSeq);
        }
        return newSelected;
      });
    } else {
      // MediaViewer 열기 - 현재 인덱스 찾기 (allMedia는 메모이제이션됨)
      const currentIndex = allMedia.findIndex(
        (media) => media.albumSeq === albumSeq
      );

      setSelectedMedia({ url: mediaUrl, type: mediaType });
      setSelectedAlbumSeq(albumSeq);
      setCurrentMediaIndex(currentIndex >= 0 ? currentIndex : 0);
      setIsMediaViewerVisible(true);
    }
  }, [isSelectMode, allMedia]);

  // 날짜별 일괄 선택
  const toggleDateSelection = (images: GalleryItem["images"]) => {
    const albumSeqs = images.map((img) => img.albumSeq);
    const allSelected = albumSeqs.every((seq) => selectedImages.has(seq));

    const newSelected = new Set(selectedImages);
    if (allSelected) {
      albumSeqs.forEach((seq) => newSelected.delete(seq));
    } else {
      albumSeqs.forEach((seq) => newSelected.add(seq));
    }
    setSelectedImages(newSelected);
  };

  // 선택한 미디어 다운로드 (이미지/동영상)
  const downloadSelectedMedia = async () => {
    try {
      // 메모이제이션된 allMedia 사용
      const selectedMediaData = allMedia.filter((media) =>
        selectedImages.has(media.albumSeq)
      );

      if (isAndroid) {
        // 안드로이드: 각 미디어를 타입에 맞게 개별 다운로드
        for (const media of selectedMediaData) {
          if (media.mediaType === "video") {
            // 동영상 다운로드
            if ((window.mung as any).downloadVideo) {
              (window.mung as any).downloadVideo(media.url);
            } else {
              window.mung.downloadImage(media.url);
            }
          } else {
            // 이미지 다운로드
            window.mung.downloadImage(media.url);
          }
        }
      } else if (isIOS) {
        // iOS: 각 미디어를 타입에 맞게 개별 다운로드
        for (const media of selectedMediaData) {
          if (media.mediaType === "video") {
            window.webkit?.messageHandlers?.mung?.postMessage([
              "downloadVideo",
              media.url,
            ]);
          } else {
            window.webkit?.messageHandlers?.mung?.postMessage([
              "downloadImage",
              media.url,
            ]);
          }
        }
      } else {
        // 웹: 각 미디어를 순차적으로 다운로드
        for (const media of selectedMediaData) {
          try {
            const response = await fetch(media.url);
            const blob = await response.blob();
            const downloadUrl = window.URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = downloadUrl;
            const extension = media.mediaType === "video" ? ".mp4" : ".jpg";
            a.download = `${media.mediaType === "video" ? "video" : "image"}-${media.albumSeq}-${Date.now()}${extension}`;
            a.target = "_blank";
            a.rel = "noopener noreferrer";
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            window.URL.revokeObjectURL(downloadUrl);

            // 다운로드 간 약간의 딜레이 (브라우저 다운로드 제한 방지)
            await new Promise((resolve) => setTimeout(resolve, 100));
          } catch (fetchError) {
            console.warn(`다운로드 실패 (${media.url}):`, fetchError);
          }
        }
      }
      // 다운로드 완료 후 선택 모드 해제
      setIsSelectMode(false);
      setSelectedImages(new Set());
    } catch (error) {
      console.error("다운로드 중 오류 발생:", error);
    }
  };

  // 로딩 상태
  if (isLoading) {
    return (
      <>
        <LayoutHeader
          title={"사진첩"}
          hasBackBtn={false}
          hasFootPrint={false}
        />
        <div className="flex justify-center items-center flex-auto">
          <Loading />
        </div>
      </>
    );
  }

  // 에러 상태
  if (error) {
    return (
      <>
        <LayoutHeader
          title={"사진첩"}
          hasBackBtn={false}
          hasFootPrint={false}
        />
        <div className="flex justify-center items-center flex-auto">
          <p className="text-red-500">
            사진을 불러오는 중 오류가 발생했습니다.
          </p>
        </div>
      </>
    );
  }

  return (
    <>
      {!selectedMedia && (
        <LayoutHeader
          title={"사진첩"}
          hasBackBtn={false}
          hasFootPrint={false}
        />
      )}
      <div className="flex flex-col flex-auto bg-[#F5F5F5]">
        <div className="flex justify-end items-center px-4 py-3">
          {/* <span className="text-lg font-medium">사진첩</span> */}
          <div className="flex gap-2">
            <button
              onClick={() => {
                setIsSelectMode(!isSelectMode);
                setSelectedImages(new Set());
              }}
              className="text-gray-600"
            >
              {isSelectMode ? "취소" : "선택"}
            </button>
            {isSelectMode && selectedImages.size > 0 && (
              <button
                onClick={downloadSelectedMedia}
                className="flex gap-1 items-center text-orange-500"
              >
                <IconDownload className="w-5 h-5" />
                <span>다운로드</span>
              </button>
            )}
          </div>
        </div>

        {/* 갤러리 목록 - 가상화 적용 */}
        <div
          ref={parentRef}
          className="overflow-auto flex-1"
        >
          {galleryData.length > 0 ? (
            <div
              className="relative"
              style={{
                height: `${rowVirtualizer.getTotalSize()}px`,
              }}
            >
              {rowVirtualizer.getVirtualItems().map((virtualRow: VirtualItem) => {
                const group = galleryData[virtualRow.index];
                const allSelected = group.images.every((img) =>
                  selectedImages.has(img.albumSeq)
                );
                return (
                  <div
                    key={virtualRow.key}
                    data-index={virtualRow.index}
                    ref={(el) => {
                      if (el) {
                        rowRefs.current.set(virtualRow.index, el);
                        // ResizeObserver에 추가
                        if (imageLoadObserverRef.current) {
                          imageLoadObserverRef.current.observe(el);
                        }
                        // 초기 높이 측정
                        requestAnimationFrame(() => {
                          rowVirtualizer.measureElement(el);
                        });
                      } else {
                        rowRefs.current.delete(virtualRow.index);
                      }
                    }}
                    className="absolute top-0 left-0 w-full px-4"
                    style={{
                      transform: `translateY(${virtualRow.start}px)`,
                    }}
                  >
                    <div className="flex flex-col gap-2 pb-6">
                      <div className="flex gap-2 items-center">
                        {isSelectMode && (
                          <div
                            className={`w-4 h-4 rounded-full border-2 flex items-center justify-center cursor-pointer
                              ${allSelected ? "bg-orange-500 border-orange-500" : "border-gray-300 bg-white"}`}
                            onClick={() => toggleDateSelection(group.images)}
                          >
                            {allSelected && <span className="text-sm text-white">✓</span>}
                          </div>
                        )}
                        <span className="text-sm text-gray-600">{group.date}</span>
                      </div>
                      <div className="grid grid-cols-4 gap-2 sm:grid-cols-4">
                        {group.images.map((media, mediaIndex) => (
                          <CustomerMediaItem
                            key={media.albumSeq}
                            media={media}
                            mediaIndex={mediaIndex}
                            isSelectMode={isSelectMode}
                            isSelected={selectedImages.has(media.albumSeq)}
                            onSelect={toggleMediaSelection}
                          />
                        ))}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="py-8 text-center text-gray-500">
              사진이 없습니다.
            </div>
          )}
        </div>
      </div>
      {isMediaViewerVisible && selectedMedia && (
        <ImageViewer
          imageUrl={selectedMedia.url}
          isVisible={isMediaViewerVisible}
          onClose={closeMediaViewer}
          onDownload={handleMediaDownload}
          mediaType={selectedMedia.type}
          images={allMediaUrls}
          currentIndex={currentMediaIndex}
          defaultMutedList={allMedia.map((media) => media.muteYn === "Y")}
          onImageChange={(index) => {
            setCurrentMediaIndex(index);
            if (allMedia[index]) {
              setSelectedMedia({
                url: allMedia[index].url,
                type: allMedia[index].mediaType,
              });
              setSelectedAlbumSeq(allMedia[index].albumSeq);
            }
          }}
        />
      )}
    </>
  );
}
